import { useCallback, useEffect, useState } from 'react';

function detectIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
}

function detectAndroid() {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent || '');
}

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true
  );
}

/**
 * Lightweight PWA install detection — no-op when unsupported.
 */
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const [isIos] = useState(detectIos);
  const [isAndroid] = useState(detectAndroid);

  useEffect(() => {
    setIsStandalone(detectStandalone());

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const onDisplayMode = () => setIsStandalone(detectStandalone());

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.matchMedia('(display-mode: standalone)').addEventListener?.('change', onDisplayMode);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.matchMedia('(display-mode: standalone)').removeEventListener?.('change', onDisplayMode);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { ok: false, reason: 'unavailable' };
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsStandalone(detectStandalone());
      }
      return { ok: outcome === 'accepted', reason: outcome };
    } catch {
      return { ok: false, reason: 'error' };
    }
  }, [deferredPrompt]);

  return {
    canPromptInstall: Boolean(deferredPrompt),
    isStandalone,
    isIos,
    isAndroid,
    promptInstall,
    showGuidance: !isStandalone,
  };
}
